import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { Eyebrow, LuxeButton, ChapterHeading } from "@/components/shared";

const About = () => (
  <>
    <Seo
      title="About Soby Scherif | 25+ Years of Luxury Real Estate — Scherif Real Estate"
      siteName="Scherif Real Estate"
      description="Meet Sobeida “Soby” Scherif — founder of Scherif Real Estate, serving DC, Maryland, Virginia, and the Dominican Republic with a direct, transparent, strategic approach."
    />

    <section className="container-luxe pb-24 pt-36 sm:pt-48" data-testid="about-hero">
      <div className="grid items-center gap-14 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5">
          <div className="relative">
            <div className="img-zoom border border-warm-border p-3">
              <img src="/images/soby-portrait.webp" alt="Sobeida “Soby” Scherif, founder of Scherif Real Estate" className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-4 bg-charcoal px-8 py-5 sm:-right-8">
              <p className="font-serif text-4xl font-light text-champagne">25<span className="text-2xl">+</span></p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-ivory/70">Years of Real Estate Experience</p>
            </div>
          </div>
        </FadeUp>
        <div className="lg:col-span-7 lg:pl-10">
          <ChapterHeading eyebrow="About Us" title={<>Meet Sobeida “Soby” Scherif</>} />
          <FadeUp delay={0.15}>
            <p className="mt-8 text-base leading-relaxed text-slate-muted">
              With more than 25 years of experience in real estate, Soby Scherif is committed to being the trusted advisor her clients deserve.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-muted">
              She understands that buying or selling a home is a significant and personal journey — one that carries both financial and emotional
              weight. Soby and her team&apos;s extensive expertise and deep understanding of the real estate market empower them to excel in their
              roles, allowing them to advocate effectively for clients and deliver the best possible results.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-muted">
              Two years ago, Soby founded Scherif Real Estate to expand internationally. This move has allowed her to hone her brand and grow her
              clientele through a clear vision and consistent approach.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-muted">
              The journey from being qualified to truly successful in real estate takes time, and Soby&apos;s commitment to directness, transparency,
              and expert negotiation has earned her numerous 5-star client reviews.
            </p>
            <p className="mt-5 text-base leading-relaxed text-slate-muted">
              Her ability to keep transactions on track — calmly and professionally — ensures that clients feel fully supported throughout their real
              estate journey.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <LuxeButton to="/contact" variant="dark" testId="about-contact-btn">Start a Conversation</LuxeButton>
              <LuxeButton href="tel:+12403724804" variant="outline-dark" testId="about-call-btn">Call 240-372-4804</LuxeButton>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <Marquee items={["Direct", "Transparent", "Strategic", "Scherif Real Estate", "Soby & Associates"]} />

    <section className="bg-obsidian py-24 sm:py-36" data-testid="philosophy-section">
      <div className="container-luxe">
        <div className="mb-16 max-w-3xl">
          <ChapterHeading eyebrow="The Philosophy" title="Direct. Transparent. Strategic." dark />
          <FadeUp delay={0.15}>
            <p className="mt-8 text-base leading-relaxed text-ivory/70">
              Three words that govern every client relationship, every negotiation, and every transaction we touch.
            </p>
          </FadeUp>
        </div>
        <Stagger className="grid gap-12 md:grid-cols-3 md:gap-8">
          {[
            { n: "01", title: "Direct", copy: "Honest counsel, even when it is not the easy answer. You will always know where you stand — on pricing, positioning, and the path ahead." },
            { n: "02", title: "Transparent", copy: "Clear communication from first meeting to closing day. No surprises, no silence — every step explained, every question answered." },
            { n: "03", title: "Strategic", copy: "Every decision is deliberate. Pricing, marketing, timing, and negotiation are coordinated toward a single outcome: your strongest possible result." },
          ].map((c) => (
            <StaggerItem key={c.n} className="border-t border-white/15 pt-8">
              <span className="font-serif text-3xl font-light italic text-ivory/20">{c.n}</span>
              <h3 className="mt-5 font-serif text-3xl font-light text-ivory">{c.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-ivory/65">{c.copy}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <section className="container-luxe py-24 sm:py-36" data-testid="about-editorial">
      <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-6">
          <FadeUp>
            <Eyebrow className="mb-4">The Brand</Eyebrow>
            <h2 className="font-serif text-4xl font-light leading-[1.1] tracking-tight text-charcoal sm:text-5xl">
              A Regional Brokerage With an International Reach
            </h2>
            <p className="mt-8 text-base leading-relaxed text-slate-muted">
              Scherif Real Estate serves Washington, DC, Maryland, and Virginia — and, through Soby &amp; Associates, extends to the Dominican
              Republic and luxury destinations beyond. One standard of representation, across every market we touch.
            </p>
            <div className="mt-10">
              <LuxeButton to="/vacation-homes" variant="dark" testId="about-vacation-btn">Explore the International Portfolio</LuxeButton>
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-6">
          <div className="img-zoom">
            <img src="/images/soby-interior.webp" alt="Soby Scherif in a client's elegant living room" className="aspect-[4/5] w-full object-cover" loading="lazy" />
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

export default About;
